import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarDays, Pencil, Trash2, Plus, FileSpreadsheet, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TOOLTIP_STYLE = { background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12, color: '#0A0A0A' };
const AXIS_TICK = { fontSize: 10, fill: '#6B7280' };
const BAR_RADIUS = [4, 4, 0, 0];
const LINE_DOT = { fill: '#0082D2', r: 3 };

export default function DiferenciaFonasa() {
  const { isAuditor } = useAuth();
  const [stats, setStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());
  const [monthlyPrevYear, setMonthlyPrevYear] = useState(new Date().getFullYear() - 1);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [fecha, setFecha] = useState(null);
  const [altas, setAltas] = useState('');
  const [bajas, setBajas] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statsData, monthly] = await Promise.all([
        api.getDiferenciaStats(),
        api.getDiferenciaMonthly(monthlyYear)
      ]);
      setStats(statsData);
      setMonthlyData(monthly.data || []);
      setMonthlyPrevYear(monthly.año_prev);
    }
    catch (error) { /* handled */ }
    finally { setLoading(false); }
  }, [monthlyYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDialog = (entry = null) => {
    if (entry) { setEditingDate(entry.fecha); setFecha(new Date(entry.fecha + 'T12:00:00')); setAltas(entry.altas.toString()); setBajas(entry.bajas.toString()); }
    else { setEditingDate(null); setFecha(new Date()); setAltas(''); setBajas(''); }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fecha || altas === '' || bajas === '') { toast.error('Completa todos los campos'); return; }
    try {
      await api.createDiferenciaFonasa({ fecha: format(fecha, 'yyyy-MM-dd'), altas: parseInt(altas) || 0, bajas: parseInt(bajas) || 0 });
      toast.success(editingDate ? 'Datos actualizados' : 'Datos registrados');
      setShowDialog(false); fetchData();
    } catch (error) { const detail = error.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Error al guardar'); }
  };

  const handleDelete = async (fecha) => {
    if (!window.confirm('Eliminar este registro?')) return;
    try { await api.deleteDiferenciaFonasa(fecha); toast.success('Registro eliminado'); fetchData(); }
    catch (error) { toast.error('Error al eliminar'); }
  };

  const chartData = stats?.daily?.map(d => ({ fecha: d.fecha.substring(5), altas: d.altas, bajas: d.bajas, saldo: d.saldo_acumulado })) || [];

  const exportToExcel = () => {
    if (!stats?.daily?.length) { toast.error('No hay datos para exportar'); return; }
    const data = stats.daily.map(d => ({ 'Fecha': new Date(d.fecha + 'T12:00:00').toLocaleDateString('es-UY'), 'Altas': d.altas, 'Bajas': d.bajas, 'Saldo Dia': d.saldo_dia, 'Saldo Acumulado': d.saldo_acumulado }));
    data.push({ 'Fecha': 'TOTALES', 'Altas': stats.total_altas, 'Bajas': stats.total_bajas, 'Saldo Dia': stats.saldo_mes, 'Saldo Acumulado': '' });
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Diferencia Fonasa');
    XLSX.writeFile(wb, `diferencia_fonasa_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado');
  };

  const exportToPDF = () => {
    if (!stats?.daily?.length) { toast.error('No hay datos para exportar'); return; }
    const doc = new jsPDF();
    doc.setFontSize(18); doc.text('Diferencia Fonasa', 14, 22);
    doc.setFontSize(10); doc.text(`Generado: ${new Date().toLocaleDateString('es-UY')}`, 14, 30);
    doc.text(`Total Altas: ${stats.total_altas} | Total Bajas: ${stats.total_bajas} | Saldo: ${stats.saldo_mes}`, 14, 37);
    const tableData = stats.daily.map(d => [new Date(d.fecha + 'T12:00:00').toLocaleDateString('es-UY'), d.altas, d.bajas, d.saldo_dia, d.saldo_acumulado]);
    autoTable(doc, { head: [['Fecha', 'Altas', 'Bajas', 'Saldo Dia', 'Saldo Acum.']], body: tableData, startY: 42, styles: { fontSize: 9 }, headStyles: { fillColor: [0, 32, 91] } });
    doc.save(`diferencia_fonasa_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exportado');
  };

  return (
    <div className="space-y-6" data-testid="diferencia-fonasa">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Diferencia Fonasa</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Registro diario de altas y bajas</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportToExcel} className="btn-secondary flex items-center gap-2" data-testid="fonasa-export-excel"><FileSpreadsheet size={16} /> Excel</button>
          <button onClick={exportToPDF} className="btn-secondary flex items-center gap-2" data-testid="fonasa-export-pdf"><FileText size={16} /> PDF</button>
          {isAuditor && (<button onClick={() => openDialog()} className="btn-primary flex items-center gap-2" data-testid="add-diferencia-btn"><Plus size={16} /> Agregar Dia</button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="metric-card"><div className="metric-label">Total Altas Mes</div><div className="metric-value text-[#10B981]">+{stats?.total_altas || 0}</div></div>
        <div className="metric-card"><div className="metric-label">Total Bajas Mes</div><div className="metric-value text-[#EF4444]">-{stats?.total_bajas || 0}</div></div>
        <div className="metric-card"><div className="metric-label">Saldo del Mes</div><div className={`metric-value ${(stats?.saldo_mes || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{(stats?.saldo_mes || 0) >= 0 ? '+' : ''}{stats?.saldo_mes || 0}</div></div>
      </div>

      {monthlyData.length > 0 && (
        <div className="mac-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Evolucion Mensual</h3>
            <select value={monthlyYear} onChange={e => setMonthlyYear(parseInt(e.target.value))} className="form-input text-[13px] py-1.5" style={{width: '90px'}} data-testid="monthly-year-select">
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="saldo" fill="#0082D2" name={`Saldo ${monthlyYear}`} radius={BAR_RADIUS} />
                <Bar dataKey="saldo_prev" fill="#94A3B8" name={`Saldo ${monthlyPrevYear}`} radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto mt-4">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">Mes</th>
                  <th className="table-header text-center" colSpan={3}>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#0082D2]"></span>{monthlyYear}</span>
                  </th>
                  <th className="table-header text-center" colSpan={3}>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#94A3B8]"></span>{monthlyPrevYear}</span>
                  </th>
                  <th className="table-header text-center">Dif. Saldo</th>
                </tr>
                <tr>
                  <th className="table-header"></th>
                  <th className="table-header text-center text-[10px]">Altas</th>
                  <th className="table-header text-center text-[10px]">Bajas</th>
                  <th className="table-header text-center text-[10px]">Saldo</th>
                  <th className="table-header text-center text-[10px]">Altas</th>
                  <th className="table-header text-center text-[10px]">Bajas</th>
                  <th className="table-header text-center text-[10px]">Saldo</th>
                  <th className="table-header text-center"></th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map(m => {
                  const diff = m.saldo - m.saldo_prev;
                  return (
                    <tr key={m.label} className="table-row">
                      <td className="table-cell font-medium">{m.label}</td>
                      <td className="table-cell text-center text-[#10B981] text-[13px]">+{m.altas}</td>
                      <td className="table-cell text-center text-[#EF4444] text-[13px]">-{m.bajas}</td>
                      <td className={`table-cell text-center font-semibold text-[13px] ${m.saldo >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{m.saldo >= 0 ? '+' : ''}{m.saldo}</td>
                      <td className="table-cell text-center text-[#10B981] text-[13px]">+{m.altas_prev}</td>
                      <td className="table-cell text-center text-[#EF4444] text-[13px]">-{m.bajas_prev}</td>
                      <td className={`table-cell text-center font-semibold text-[13px] ${m.saldo_prev >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{m.saldo_prev >= 0 ? '+' : ''}{m.saldo_prev}</td>
                      <td className="table-cell text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${diff > 0 ? 'bg-[#10B981]/10 text-[#10B981]' : diff < 0 ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-gray-100 text-gray-500'}`}>
                          {diff > 0 ? `+${diff}` : diff}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-gray-300">
                  <td className="table-cell font-bold text-[#00205B]">TOTAL</td>
                  <td className="table-cell text-center text-[#10B981] font-bold">+{monthlyData.reduce((s, m) => s + m.altas, 0)}</td>
                  <td className="table-cell text-center text-[#EF4444] font-bold">-{monthlyData.reduce((s, m) => s + m.bajas, 0)}</td>
                  <td className={`table-cell text-center font-bold ${monthlyData.reduce((s, m) => s + m.saldo, 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                    {monthlyData.reduce((s, m) => s + m.saldo, 0) >= 0 ? '+' : ''}{monthlyData.reduce((s, m) => s + m.saldo, 0)}
                  </td>
                  <td className="table-cell text-center text-[#10B981] font-bold">+{monthlyData.reduce((s, m) => s + m.altas_prev, 0)}</td>
                  <td className="table-cell text-center text-[#EF4444] font-bold">-{monthlyData.reduce((s, m) => s + m.bajas_prev, 0)}</td>
                  <td className={`table-cell text-center font-bold ${monthlyData.reduce((s, m) => s + m.saldo_prev, 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                    {monthlyData.reduce((s, m) => s + m.saldo_prev, 0) >= 0 ? '+' : ''}{monthlyData.reduce((s, m) => s + m.saldo_prev, 0)}
                  </td>
                  <td className="table-cell text-center">
                    {(() => { const d = monthlyData.reduce((s, m) => s + m.saldo, 0) - monthlyData.reduce((s, m) => s + m.saldo_prev, 0); return (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${d > 0 ? 'bg-[#10B981]/10 text-[#10B981]' : d < 0 ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-gray-100 text-gray-500'}`}>{d > 0 ? `+${d}` : d}</span>
                    ); })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="mac-card p-5">
            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Altas vs Bajas Diarias</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                  <XAxis dataKey="fecha" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="altas" fill="#10B981" name="Altas" radius={BAR_RADIUS} />
                  <Bar dataKey="bajas" fill="#EF4444" name="Bajas" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="mac-card p-5">
            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Saldo Acumulado</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                  <XAxis dataKey="fecha" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Line type="monotone" dataKey="saldo" stroke="#0082D2" strokeWidth={2} dot={LINE_DOT} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="mac-card overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200"><h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Detalle Diario</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><th className="table-header">Fecha</th><th className="table-header">Altas</th><th className="table-header">Bajas</th><th className="table-header">Saldo Dia</th><th className="table-header">Saldo Acumulado</th>{isAuditor && <th className="table-header">Acciones</th>}</tr></thead>
            <tbody>
              {loading ? (<tr><td colSpan={isAuditor ? 6 : 5} className="table-cell text-center text-gray-500">Cargando...</td></tr>
              ) : stats?.daily?.length === 0 ? (<tr><td colSpan={isAuditor ? 6 : 5} className="table-cell text-center text-gray-500">Sin datos</td></tr>
              ) : (stats?.daily?.map(day => (
                <tr key={day.fecha} className="table-row">
                  <td className="table-cell font-medium">{new Date(day.fecha + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                  <td className="table-cell text-[#10B981] font-medium">+{day.altas}</td>
                  <td className="table-cell text-[#EF4444] font-medium">-{day.bajas}</td>
                  <td className={`table-cell font-medium ${day.saldo_dia >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{day.saldo_dia >= 0 ? '+' : ''}{day.saldo_dia}</td>
                  <td className={`table-cell font-semibold ${day.saldo_acumulado >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{day.saldo_acumulado >= 0 ? '+' : ''}{day.saldo_acumulado}</td>
                  {isAuditor && (<td className="table-cell"><div className="flex items-center gap-1"><button onClick={() => openDialog(day)} className="btn-ghost p-1.5" data-testid={`edit-${day.fecha}`}><Pencil size={14} /></button><button onClick={() => handleDelete(day.fecha)} className="btn-ghost p-1.5 text-[#EF4444] hover:text-[#EF4444]" data-testid={`delete-${day.fecha}`}><Trash2 size={14} /></button></div></td>)}
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="mac-popover max-w-sm p-0">
          <DialogHeader className="px-5 py-4 border-b border-gray-200"><DialogTitle className="text-[15px] font-semibold text-[#0A0A0A]">{editingDate ? 'Editar Dia' : 'Agregar Dia'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="form-label">Fecha</label>
              <Popover><PopoverTrigger asChild><button type="button" className="form-input flex items-center justify-between text-left" data-testid="diferencia-fecha">{fecha ? format(fecha, 'dd/MM/yyyy') : <span className="text-gray-400">Seleccionar</span>}<CalendarDays size={16} className="text-gray-400" /></button></PopoverTrigger><PopoverContent className="w-auto p-0 mac-popover" align="start"><Calendar mode="single" selected={fecha} onSelect={setFecha} locale={es} initialFocus className="rounded-md" /></PopoverContent></Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Altas</label><input type="number" min="0" value={altas} onChange={(e) => setAltas(e.target.value)} className="form-input" placeholder="0" data-testid="diferencia-altas" /></div>
              <div><label className="form-label">Bajas</label><input type="number" min="0" value={bajas} onChange={(e) => setBajas(e.target.value)} className="form-input" placeholder="0" data-testid="diferencia-bajas" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowDialog(false)} className="btn-secondary">Cancelar</button>
              <button type="submit" className="btn-primary" data-testid="save-diferencia-btn">Guardar</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
